import { useEffect, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Spinner,
  Banner,
  DataTable,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface DashboardStats {
  shops: {
    total: number;
    active: number;
    churned: number;
  };
  users: {
    total: number;
  };
  revenue: {
    mrr: number;
    currency: string;
  };
  thisMonth: {
    newInstalls: number;
    cancellations: number;
  };
  planDistribution: Array<{
    plan: string;
    count: number;
  }>;
}

export default function AdminDashboard() {
  const { fetchApi, error } = useApi();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    const response = await fetchApi<{ success: boolean; stats: DashboardStats }>('/super-admin/stats');
    if (response?.success) {
      setStats(response.stats);
    }
    setLoading(false);
  }

  if (loading) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <Card>
              <InlineStack align="center" blockAlign="center">
                <Spinner size="large" />
              </InlineStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  return (
    <Page title="Dashboard">
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Stats Cards */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false}>
            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">Active Shops</Text>
                <Text as="p" variant="heading2xl">{stats?.shops.active || 0}</Text>
                <InlineStack gap="200">
                  <Badge tone="success">{`${stats?.shops.active || 0} active`}</Badge>
                  <Badge tone="critical">{`${stats?.shops.churned || 0} churned`}</Badge>
                </InlineStack>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">Total Users</Text>
                <Text as="p" variant="heading2xl">{stats?.users.total || 0}</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Across all shops
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">Monthly Revenue</Text>
                <Text as="p" variant="heading2xl">
                  {formatCurrency(stats?.revenue.mrr || 0)}
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  MRR (Monthly Recurring)
                </Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h3" variant="headingMd">This Month</Text>
                <InlineStack gap="400">
                  <BlockStack gap="100">
                    <Text as="p" variant="headingLg" tone="success">
                      +{stats?.thisMonth.newInstalls || 0}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">New</Text>
                  </BlockStack>
                  <BlockStack gap="100">
                    <Text as="p" variant="headingLg" tone="critical">
                      -{stats?.thisMonth.cancellations || 0}
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">Churned</Text>
                  </BlockStack>
                </InlineStack>
              </BlockStack>
            </Card>
          </InlineStack>
        </Layout.Section>

        {/* Plan Distribution */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingLg">Plan Distribution</Text>
              {stats?.planDistribution && stats.planDistribution.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric']}
                  headings={['Plan', 'Shops', 'Percentage']}
                  rows={stats.planDistribution.map((item) => {
                    const total = stats.shops.active || 1;
                    const percentage = ((item.count / total) * 100).toFixed(1);
                    return [
                      <Badge key={item.plan} tone={item.plan === 'free' ? 'attention' : 'success'}>
                        {item.plan || 'Free'}
                      </Badge>,
                      item.count.toString(),
                      `${percentage}%`,
                    ];
                  })}
                />
              ) : (
                <Text as="p" tone="subdued">No shops yet</Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Quick Links */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingLg">Quick Actions</Text>
              <BlockStack gap="200">
                <QuickAction href="/admin/shops" label="View All Shops" />
                <QuickAction href="/admin/plans" label="Manage Pricing" />
                <QuickAction href="/admin/revenue" label="Revenue Analytics" />
                <QuickAction href="/admin/admins" label="Manage Admins" />
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingLg">System Health</Text>
              <BlockStack gap="200">
                <InlineStack gap="200" blockAlign="center">
                  <Badge tone="success">Healthy</Badge>
                  <Text as="span">API</Text>
                </InlineStack>
                <InlineStack gap="200" blockAlign="center">
                  <Badge tone="success">Connected</Badge>
                  <Text as="span">Database</Text>
                </InlineStack>
                <InlineStack gap="200" blockAlign="center">
                  <Badge tone="success">Active</Badge>
                  <Text as="span">Webhooks</Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function QuickAction({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      style={{
        display: 'block',
        padding: '0.75rem 1rem',
        border: '1px solid #e1e3e5',
        borderRadius: '8px',
        textDecoration: 'none',
        color: '#202223',
        background: 'white',
      }}
    >
      {label} &rarr;
    </a>
  );
}
